import React, { useState, useEffect } from 'react';
import { PokemonCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { QuantityBadge } from './QuantityBadge';
import { useTranslation } from '../../hooks/useTranslation';
import { supabase } from '../../lib/supabase';
import { CheckCircle } from 'lucide-react';

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
}

interface CardGridProps {
  cards: PokemonCard[];
  selectedCards: PokemonCard[];
  cardQuantities: Record<number, number>;
  onCardClick: (card: PokemonCard) => void;
  className?: string;
  onSelectAll?: (cards: PokemonCard[]) => void;
  showSelectAll?: boolean;
}

export const CardGrid = ({ 
  cards, 
  selectedCards, 
  cardQuantities, 
  onCardClick, 
  className = '',
  onSelectAll,
  showSelectAll = false
}: CardGridProps) => {
  const { t, lang } = useTranslation();
  const [setsInfo, setSetsInfo] = useState<Record<string, SetInfo>>({});
  
  // Récupérer les informations des sets
  useEffect(() => {
    const fetchSetsInfo = async () => {
      // Extraire tous les noms de sets uniques des cartes
      const uniqueSets = [...new Set(cards.map(card => card.set).filter(Boolean))];
      if (uniqueSets.length === 0) return;
      
      try {
        const { data } = await supabase
          .from('sets')
          .select('name, display_name, display_name_en')
          .in('name', uniqueSets);
          
        if (data) {
          const setsMap = data.reduce((acc, set) => {
            acc[set.name] = set;
            return acc;
          }, {} as Record<string, SetInfo>);
          setSetsInfo(setsMap);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations des sets:', error);
      }
    };
    
    fetchSetsInfo();
  }, [cards]);
  
  const getCardName = (card: PokemonCard): string => {
    if (lang === 'en') {
      return card.card_en || card.card_fr || '';
    }
    return card.card_fr || card.card_en || '';
  };
  
  const getImageUrl = (card: PokemonCard): string => {
    const basePath = `${SUPABASE_IMAGE_BASE_URL}`;
    const imagePath = card.image || '';
    
    if (lang === 'en') {
      return `${basePath}en/${imagePath}`;
    }
    
    return `${basePath}${imagePath}`;
  };
  
  const getSetDisplayName = (setName: string | null): string => {
    if (!setName) return '';
    
    const setInfo = setsInfo[setName];
    if (!setInfo) return setName;
    
    return lang === 'en' 
      ? (setInfo.display_name_en || setInfo.display_name) 
      : setInfo.display_name;
  };
  
  const notOwnedText = lang === 'en' ? 'Not owned' : 'Non possédée';
  
  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll(cards);
    }
  };

  return (
    <div className="max-h-[600px] overflow-y-auto">
      {showSelectAll && cards.length > 0 && (
        <div className="flex justify-end mb-3">
          <button
            onClick={handleSelectAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            {t('create_trade.actions.select_all', { count: cards.length })}
          </button>
        </div>
      )}
      
      <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 ${className}`}>
        {cards.map((card) => (
          <div
            key={card.id}
            className={`relative flex flex-col p-2 rounded-lg cursor-pointer transition-colors ${
              selectedCards.some(c => c.id === card.id)
                ? 'bg-blue-50 border-2 border-blue-500'
                : 'bg-gray-50 border border-gray-200 hover:border-blue-300'
            }`}
            onClick={() => onCardClick(card)}
          >
            <QuantityBadge quantity={cardQuantities[card.id] || 0} />
            <img
              src={getImageUrl(card)}
              alt={getCardName(card)}
              className="w-full h-32 object-contain"
            />
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {getCardName(card)}
              </p>
              <p className="text-xs text-gray-500">
                {getSetDisplayName(card.set)} • {card.rarity}
              </p>
              {cardQuantities[card.id] === 0 && (
                <p className="text-xs text-red-500 mt-1">
                  {notOwnedText}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 